import React, { useState } from 'react';
import { MedicalDataTypes } from '../../../../../primary-record-types';
import useMedicalProfileData from '../../../../../hooks/api/useMedicalProfileData';
import { getIntl } from '../../../../../utils/intl';
import useResource from '../../../../../hooks/api/useResource';
import { pluralResourceCategoryMessages, resourceCategoryMessages } from '../../../../../utils/sharedMessages';
import useIdList from '../../../../../hooks/useIdList';
import ResourceSearch from '../../../../shared/ResourceSearch/ResourceSearch';
import ResourceListCard from '../../../../shared/ResourceSearch/ResourceListCard';
import { useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import Stack from '../../../../v2BaseComponents/Stack';
import { useListEditActions } from '../../../../../hooks/useListEditActions';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';

const intl = getIntl('RelatedResourceListTab', {
    add: 'Add',
    link: 'Link',
    cancel: 'Cancel',
    save: 'Save',
    updateMessage: 'add {add} And remove {remove}',
});

export type RelatedResourceListTabOwnProps = {
    sourceResourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory;
    relatedResourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory;
    hasEditPermission: boolean;
    relatedResources: MedicalDataTypes.PrimaryRecordResource[];
    isLoadingRelatedResources: boolean;
    profileId: string;
    sourceResourceId: string;
};

export type RelatedResourceListTabProps = RelatedResourceListTabOwnProps;

type ListMode = 'related' | 'link';

const RelatedResourceListTab: React.FC<RelatedResourceListTabProps> = ({
    relatedResourceCategory,
    hasEditPermission,
    relatedResources,
    isLoadingRelatedResources,
    profileId,
    sourceResourceId,
    sourceResourceCategory,
}) => {
    const defaultRelatedResourceIds = [...relatedResources].map((resource) => resource.id);
    const { idList, addId, removeId, updateMessage, resetIdList } = useIdList({
        initialIdList: defaultRelatedResourceIds,
        getUpdateMessage: ({ addCount, removeCount, isAddPlural, isRemovePlural }) => {
            const getResourceMessage = (
                isPlural: boolean,
                count: number,
                category: MedicalDataTypes.PrimaryRecordResourceCategory,
            ) => {
                const resourceMessage = !isPlural
                    ? resourceCategoryMessages(category)
                    : pluralResourceCategoryMessages(category);
                return `${count} ${resourceMessage}`;
            };

            return intl('updateMessage', {
                add: getResourceMessage(isAddPlural, addCount, relatedResourceCategory).toLocaleLowerCase(),
                remove: getResourceMessage(isRemovePlural, removeCount, relatedResourceCategory).toLocaleLowerCase(),
            });
        },
    });
    const navigation = useProfilesTabNavigation();
    const [listMode, setListMode] = useState<ListMode>('related');

    const { groupedResources, isLoadingResources } = useMedicalProfileData({
        medicalProfileId: profileId,
        resourceCategory: relatedResourceCategory,
    });
    const { updateRelatedResources, isUpdatingRelatedResources } = useResource({
        medicalProfileId: profileId,
        resourceId: sourceResourceId,
        resourceCategory: sourceResourceCategory,
        relatedResourceCategories: [],
        onUpdateRelatedResourcesSuccess: () => setListMode('related'),
    });
    const listEditActions = useListEditActions({
        canEdit: hasEditPermission,
        listMode,
        onEdit: () => setListMode('link'),
        onCancel: () => {
            setListMode('related');
            resetIdList();
        },
        onAdd: () => {
            navigation.navigate('add-data', {
                profileId,
                category: relatedResourceCategory,
                sourceResourceId: sourceResourceId || null,
            });
        },
        onSubmitLinkUpdates: () => {
            updateRelatedResources({
                relatedResourceIds: idList,
                relatedResourceCategory,
            });
        },
        isDisabled: isUpdatingRelatedResources,
        promptChangeDescription: updateMessage,
    });

    const renderList = () => {
        if (listMode === 'related') {
            return (
                <ResourceSearch
                    category={relatedResourceCategory}
                    resources={relatedResources || []}
                    isLoadingResources={isLoadingRelatedResources}
                    disableDetailedSearch
                    linkEmptyStateHint
                    renderFilteredResources={(resource) => (
                        <ResourceListCard
                            isSelectable={false}
                            resource={resource}
                            category={relatedResourceCategory}
                            profileId={profileId}
                            onNavigateToDetails={() => {
                                navigation.navigate('data-details', {
                                    profileId,
                                    category: relatedResourceCategory,
                                    resourceId: resource.id,
                                });
                            }}
                        />
                    )}
                />
            );
        } else {
            return (
                <ResourceSearch
                    linkEmptyStateHint
                    category={relatedResourceCategory}
                    resources={groupedResources || []}
                    isLoadingResources={isLoadingResources}
                    renderFilteredResources={(resource) => {
                        const isSelected = idList.includes(resource.id);
                        return (
                            <ResourceListCard
                                isSelectable={true}
                                resource={resource}
                                category={relatedResourceCategory}
                                profileId={profileId}
                                isDisabled={isUpdatingRelatedResources}
                                isSelected={isSelected}
                                onSelect={() => {
                                    isSelected ? removeId(resource.id) : addId(resource.id);
                                }}
                            />
                        );
                    }}
                />
            );
        }
    };
    return (
        <>
            <ActionButtonLayout actions={listEditActions.actions}>
                <Stack vertical space={2} fullHeight fullWidth>
                    {renderList()}
                </Stack>
            </ActionButtonLayout>
            {listEditActions.Prompt}
        </>
    );
};

export default React.memo(RelatedResourceListTab);
